/**
 * Generates pagination items (pages and ellipses) based on current page and total pages
 */
export function generatePaginationItems(
  currentPage: number,
  totalPages: number,
) {
  // If 6 or fewer pages, show all pages
  if (totalPages <= 6) {
    return Array.from({ length: totalPages }, (_, i) => ({
      type: 'page' as const,
      value: i + 1,
    }))
  }

  const items: Array<{ type: 'page' | 'ellipsis'; value: number | string }> = []
  const addedPages = new Set<number>()

  // Always include the first page
  items.push({ type: 'page', value: 1 })
  addedPages.add(1)

  // Calculate the range of pages to display around the current page
  const leftSibling = Math.max(2, currentPage - 1)
  const rightSibling = Math.min(totalPages - 1, currentPage + 1)

  // Check if we need an ellipsis after page 1
  if (leftSibling > 2) {
    items.push({ type: 'ellipsis', value: 'start-ellipsis' })
  } else if (leftSibling === 2) {
    items.push({ type: 'page', value: 2 })
    addedPages.add(2)
  }

  // Add pages around current page (and the current page itself)
  for (let i = leftSibling; i <= rightSibling; i++) {
    // Skip if we already added this page or if it's the first/last page
    if (!addedPages.has(i) && i !== totalPages) {
      items.push({ type: 'page', value: i })
      addedPages.add(i)
    }
  }

  // Check if we need an ellipsis before the last page
  if (rightSibling < totalPages - 1) {
    items.push({ type: 'ellipsis', value: 'end-ellipsis' })
  }

  // Always include the last page (unless it's already been added)
  if (!addedPages.has(totalPages)) {
    items.push({ type: 'page', value: totalPages })
  }

  return items
}
